import { useState, useMemo } from "react";
import {
  calculateHEM,
  CREDIT_CARD_ASSESSMENT_RATE,
  APRA_BUFFER,
  type HouseholdType,
  type Location,
} from "../_data/hemBenchmarks";

export interface BorrowingPowerInputs {
  // Income
  grossIncome: number;          // Annual gross income
  partnerIncome: number;        // Partner's annual gross income
  otherIncome: number;          // Rental, investments, etc.

  // Household
  householdType: HouseholdType;
  dependents: number;
  location: Location;

  // Existing debts
  creditCardLimits: number;     // Total credit card limits
  existingLoans: number;        // Monthly payments on existing loans
  hecsDebt: number;             // HECS/HELP debt balance

  // Loan preferences
  interestRate: number;         // Expected interest rate (%)
  loanTerm: number;             // Years

  // Manual overrides
  manualLoanAmount: number | null;  // User-specified loan amount (null = use calculated)
}

export interface BorrowingPowerResults {
  maxBorrowing: number;
  calculatedBorrowing: number;  // Original calculated value before override
  monthlyRepayment: number;
  assessmentRate: number;
  monthlyIncome: number;
  monthlyExpenses: number;
  monthlyDebtCommitments: number;
  netServiceability: number;
  bufferApplied: number;
  isManualOverride: boolean;
}

const defaultInputs: BorrowingPowerInputs = {
  grossIncome: 100000,
  partnerIncome: 0,
  otherIncome: 0,
  householdType: "single",
  dependents: 0,
  location: "metro",
  creditCardLimits: 5000,
  existingLoans: 0,
  hecsDebt: 0,
  interestRate: 6.0,
  loanTerm: 30,
  manualLoanAmount: null,
};

/**
 * Calculate tax on income (simplified 2024-25 rates)
 */
function calculateTax(income: number): number {
  if (income <= 18200) return 0;
  if (income <= 45000) return (income - 18200) * 0.16;
  if (income <= 135000) return 4288 + (income - 45000) * 0.30;
  if (income <= 190000) return 31288 + (income - 135000) * 0.37;
  return 51638 + (income - 190000) * 0.45;
}

/**
 * Calculate HECS/HELP repayment (simplified 2024-25 rates)
 */
function calculateHECSRepayment(income: number, hecsDebt: number): number {
  if (hecsDebt === 0 || income < 54435) return 0;
  if (income <= 62850) return income * 0.01;
  if (income <= 66620) return income * 0.02;
  if (income <= 70618) return income * 0.025;
  if (income <= 74855) return income * 0.03;
  if (income <= 79346) return income * 0.035;
  if (income <= 84107) return income * 0.04;
  if (income <= 89154) return income * 0.045;
  if (income <= 94503) return income * 0.05;
  if (income <= 100174) return income * 0.055;
  if (income <= 106185) return income * 0.06;
  if (income <= 112556) return income * 0.065;
  if (income <= 119309) return income * 0.07;
  if (income <= 126467) return income * 0.075;
  if (income <= 134056) return income * 0.08;
  if (income <= 142100) return income * 0.085;
  if (income <= 150626) return income * 0.09;
  if (income <= 159663) return income * 0.095;
  return income * 0.10;
}

/**
 * Calculate maximum loan from monthly available income
 * Using PMT formula solved for PV (present value)
 */
function calculateMaxLoan(
  monthlyAvailable: number,
  annualRate: number,
  years: number
): number {
  if (monthlyAvailable <= 0) return 0;

  const monthlyRate = annualRate / 100 / 12;
  const periods = years * 12;

  if (monthlyRate === 0) {
    return monthlyAvailable * periods;
  }

  // PV = PMT * [(1 - (1 + r)^-n) / r]
  const maxLoan =
    monthlyAvailable * ((1 - Math.pow(1 + monthlyRate, -periods)) / monthlyRate);

  return Math.max(0, maxLoan);
}

/**
 * Calculate monthly repayment for a loan
 */
function calculateMonthlyRepayment(
  principal: number,
  annualRate: number,
  years: number
): number {
  const monthlyRate = annualRate / 100 / 12;
  const periods = years * 12;

  if (monthlyRate === 0) {
    return principal / periods;
  }

  return (
    (principal * (monthlyRate * Math.pow(1 + monthlyRate, periods))) /
    (Math.pow(1 + monthlyRate, periods) - 1)
  );
}

/**
 * Borrowing power calculator hook
 */
export function useBorrowingPowerCalculator() {
  const [inputs, setInputs] = useState<BorrowingPowerInputs>(defaultInputs);

  const results = useMemo<BorrowingPowerResults>(() => {
    // Calculate total gross income
    const totalGrossIncome = inputs.grossIncome + inputs.partnerIncome + inputs.otherIncome;

    // Calculate after-tax income
    const primaryTax = calculateTax(inputs.grossIncome);
    const partnerTax = calculateTax(inputs.partnerIncome);
    const totalTax = primaryTax + partnerTax; // Other income simplified

    const annualNetIncome = totalGrossIncome - totalTax;
    const monthlyNetIncome = annualNetIncome / 12;

    // Calculate HECS repayment (based on primary income)
    const annualHECS = calculateHECSRepayment(inputs.grossIncome, inputs.hecsDebt);
    const monthlyHECS = annualHECS / 12;

    // Calculate living expenses using HEM
    const monthlyExpenses = calculateHEM(
      inputs.householdType,
      inputs.dependents,
      inputs.location
    );

    // Calculate debt commitments
    const creditCardCommitment = inputs.creditCardLimits * CREDIT_CARD_ASSESSMENT_RATE;
    const monthlyDebtCommitments = creditCardCommitment + inputs.existingLoans + monthlyHECS;

    // Assessment rate (advertised + APRA buffer)
    const assessmentRate = inputs.interestRate + APRA_BUFFER * 100;

    // Net serviceability income
    const netServiceability = monthlyNetIncome - monthlyExpenses - monthlyDebtCommitments;

    // Calculate maximum borrowing
    const calculatedBorrowing = calculateMaxLoan(
      netServiceability,
      assessmentRate,
      inputs.loanTerm
    );

    // Use manual override if specified, otherwise use calculated
    const isManualOverride = inputs.manualLoanAmount !== null && inputs.manualLoanAmount > 0;
    const maxBorrowing = isManualOverride ? inputs.manualLoanAmount! : calculatedBorrowing;

    // Calculate repayment at actual rate
    const monthlyRepayment = calculateMonthlyRepayment(
      maxBorrowing,
      inputs.interestRate,
      inputs.loanTerm
    );

    return {
      maxBorrowing: Math.round(maxBorrowing),
      calculatedBorrowing: Math.round(calculatedBorrowing),
      monthlyRepayment: Math.round(monthlyRepayment * 100) / 100,
      assessmentRate: Math.round(assessmentRate * 100) / 100,
      monthlyIncome: Math.round(monthlyNetIncome * 100) / 100,
      monthlyExpenses: Math.round(monthlyExpenses * 100) / 100,
      monthlyDebtCommitments: Math.round(monthlyDebtCommitments * 100) / 100,
      netServiceability: Math.round(netServiceability * 100) / 100,
      bufferApplied: APRA_BUFFER * 100,
      isManualOverride,
    };
  }, [inputs]);

  const updateInput = <K extends keyof BorrowingPowerInputs>(
    key: K,
    value: BorrowingPowerInputs[K]
  ) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  return {
    inputs,
    results,
    updateInput,
    setInputs,
  };
}
